/**
 * A lazy route or dialog failed to load its code chunk. After a deploy, a tab still running the
 * previous build asks for chunk filenames that no longer exist on the server, so this almost
 * always means "a new version is out" rather than a real bug.
 *
 * Matched by name and by message because the wording differs per bundler and browser: webpack and
 * Rspack throw `ChunkLoadError` ("Loading chunk 9475 failed", "Loading CSS chunk ... failed"), while
 * native dynamic `import()` throws a plain TypeError (Chrome: "Failed to fetch dynamically imported
 * module", Safari: "Importing a module script failed", Firefox: "error loading dynamically imported
 * module").
 */
const CHUNK_ERROR_MESSAGE =
  /Loading (CSS )?chunk [\w-]+ failed|Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module/i;

export const isChunkLoadError = (error: unknown): boolean => {
  if (!(error instanceof Error)) return false;
  return error.name === 'ChunkLoadError' || CHUNK_ERROR_MESSAGE.test(error.message);
};

const LAST_RELOAD_KEY = 'th_chunkErrorReloadAt';

/** A second chunk error this soon after the last automatic reload means the reload did not help. */
const RELOAD_COOLDOWN_MS = 60_000;

/**
 * Whether to reload the page automatically for a chunk error, recording the attempt if so.
 *
 * Loop-safe: if the chunk is genuinely missing from the new build too, the error comes straight back
 * after the reload, lands inside the cooldown and returns false, so the caller shows the refresh
 * screen instead of reloading forever. A timestamp rather than a plain flag so that a later deploy in
 * the same tab can still reload automatically once the cooldown has passed.
 *
 * Returns false when sessionStorage is unavailable (private mode, blocked storage): without a way to
 * record the attempt there is no loop guard, so it is safer to let the user press Refresh.
 */
export const claimChunkErrorReload = (now = Date.now()): boolean => {
  try {
    const lastReloadAt = Number(sessionStorage.getItem(LAST_RELOAD_KEY));
    if (lastReloadAt && now - lastReloadAt < RELOAD_COOLDOWN_MS) return false;
    sessionStorage.setItem(LAST_RELOAD_KEY, String(now));
    return true;
  } catch {
    return false;
  }
};
